import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, User, MonitorDown, Check } from "lucide-react";
import { waLink } from "./site";

const LeadContext = createContext(null);

export const LeadProvider = ({ children }) => {
  const [message, setMessage] = useState(null);
  const openLead = useCallback((msg) => setMessage(msg), []);
  const closeLead = useCallback(() => setMessage(null), []);

  return (
    <LeadContext.Provider value={{ openLead }}>
      {children}
      <LeadModal message={message} onClose={closeLead} />
    </LeadContext.Provider>
  );
};

export const useLead = () => useContext(LeadContext);

const LeadModal = ({ message, onClose }) => {
  const [name, setName] = useState("");
  const [installed, setInstalled] = useState(null);

  useEffect(() => {
    if (message) {
      setName("");
      setInstalled(null);
    }
  }, [message]);

  const valid = name.trim().length >= 2 && installed !== null;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const finalMessage = `${message}\n\n*Nome:* ${name.trim()}\n*App NOVA ÉRA:* ${
      installed === "sim" ? "Já instalei o app" : "Ainda não instalei o app"
    }`;
    window.open(waLink(finalMessage), "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[75] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={onClose}
          data-testid="lead-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-lime-350/25 bg-ink-900 p-8 shadow-2xl"
            data-testid="lead-modal"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-lime-350/10 text-lime-350">
                  <MessageCircle className="h-4 w-4" />
                </span>
                <h2 className="font-display text-2xl uppercase tracking-wider text-slate-100">
                  Quase lá!
                </h2>
              </div>
              <button
                onClick={onClose}
                data-testid="lead-close-button"
                aria-label="Fechar"
                className="rounded-full p-2 text-slate-500 transition-colors hover:text-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Só duas perguntas rápidas para agilizar seu atendimento no WhatsApp:
            </p>

            <form onSubmit={submit} className="mt-6 space-y-5">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                  <User className="h-3.5 w-3.5 text-lime-350" />
                  Seu nome
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="lead-name-input"
                  placeholder="Como podemos te chamar?"
                  autoFocus
                  className="mt-2 w-full rounded-xl border border-white/10 bg-ink-950 px-4 py-3.5 text-base text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-lime-350"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                  <MonitorDown className="h-3.5 w-3.5 text-lime-350" />
                  Já instalou o app NOVA ÉRA?
                </label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {[
                    { id: "sim", label: "Sim, já instalei" },
                    { id: "nao", label: "Ainda não" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setInstalled(opt.id)}
                      data-testid={`lead-installed-${opt.id}`}
                      className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3.5 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                        installed === opt.id
                          ? "border-lime-350 bg-lime-350/15 text-lime-350"
                          : "border-white/10 bg-ink-950 text-slate-400 hover:border-lime-350/40 hover:text-slate-200"
                      }`}
                    >
                      {installed === opt.id && <Check className="h-4 w-4" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={!valid}
                data-testid="lead-submit-button"
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-4 text-sm font-bold uppercase tracking-wider transition-[transform,background-color] duration-300 ${
                  valid
                    ? "bg-lime-350 text-ink-950 hover:-translate-y-0.5 glow-lime active:scale-95"
                    : "cursor-not-allowed bg-white/5 text-slate-600"
                }`}
              >
                <MessageCircle className="h-4 w-4" />
                Continuar no WhatsApp
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
